import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import { useProject } from "../app/projectContext";
import { useStudyDraft } from "../hooks/useStudyDraft";
import type { FaultRunFaultLevel } from "../services/faultLink";
import type { SldElements } from "../services/sldElements";
import {
  starterCoordinationRunCreateRequestSchema,
  type StarterCoordinationRunCreateRequest,
} from "../services/starterCoordination";
import {
  scpdKindSchema,
  starterCoordinationTypeSchema,
  startingMethodSchema,
} from "../services/starterCoordinationContract";
import { describeValidationIssue, type ValidationIssueLike } from "../utils/validationMessages";
import {
  FaultRunSelector,
  linkedDetailText as sharedLinkedDetailText,
} from "./FaultRunSelector";
import { OtherRevisionDraftNotice } from "./OtherRevisionDraftNotice";
import { SldElementPicker, sldElementsOf } from "./SldElementPicker";
import {
  COORDINATION_TYPE_LABELS,
  FAULT_LEVEL_SOURCE_LABELS,
  FAULT_LEVEL_SOURCES,
  SCPD_KIND_LABELS,
  STARTER_COORDINATION_DRAFT_MODULE,
  STARTER_COORDINATION_LABELS,
  STARTING_METHOD_LABELS,
  buildStarterCoordinationRunPayload,
  createInitialStarterCoordinationDraft,
  createStarterRowDraft,
  seedStarterRowIdCounter,
  starterCoordinationDraftSchema,
  type FaultLevelSource,
  type StarterCoordinationDraft,
  type StarterRowDraft,
} from "./starterCoordinationDraft";

/** The first option of every choice: nothing is chosen for the engineer (A20 (e)). */
const CHOOSE = "Choose…";

// The fault-run selector's words live with the shared selector since SLD-b1 (A27 (e)); they
// are re-exported here so the pages and tests that read them from this form still can.
export {
  FAULT_RUN_NOT_LINKABLE,
  NO_FAULT_RUNS_NOTE,
  NO_OPEN_REVISION_NOTE,
  NOT_MAXIMUM_THREE_PHASE_NOTE,
  busMismatchNote,
  faultRunOptionText,
} from "./FaultRunSelector";

export const TYPED_HINT =
  "A typed value is not traceable to a stored fault study; the result will be Engineering " +
  "review required.";

export const CERTIFICATE_HINT =
  "From the manufacturer's coordination test certificate or table for this exact " +
  "combination. The engine checks the declared evidence; it does not prove coordination. " +
  "Leave blank if not yet available (flagged for review).";

export const FLC_HINT = "From the motor nameplate or datasheet.";

export const NON_DOL_HINT =
  "Contactor and overload current checks are not evaluated for this starting method; the Iq " +
  "check still applies.";

export const PROFILE_NOTE =
  "The coordination checks carry no profile-dependent value; the profile is recorded with " +
  "the run and must match the design basis of the project revision.";

/** The read-only line under the selector as this study shows it: Ik'' without the peak. */
export function linkedDetailText(level: FaultRunFaultLevel): string {
  return sharedLinkedDetailText(level, false);
}

type StarterCoordinationFormProps = {
  disabled?: boolean;
  onSubmit: (payload: StarterCoordinationRunCreateRequest) => void | Promise<void>;
  // The revision's single-line diagram, when it has one (the page reads it): the MCC bus
  // and the motors are picked from it, and a picked bus prefills its motors (A27 (b), (f)).
  sldElements?: SldElements | null | undefined;
};

// The Type 1 / Type 2 starter coordination study (EOS-05a). The form owns the
// draft. The fault level starts on a linked fault run of the active open revision
// (A20 (d)); the runs are listed from the fault runs API and the chosen run's
// stored result is shown read-only, for the engineer's eyes only - the backend
// reads the same stored result again when the study is saved. A typed value is
// allowed and said to be review required. Every choice starts unchosen: the
// backend imposes no default (A20 (e)).
//
// The draft is kept under its own module key, "starter-coordination"; no form
// ever reads, restores or deletes another's draft.
//
// The payload carries no project revision: the page adds the selected revision
// when it calls the API, exactly as the other study pages do.
export function StarterCoordinationForm({
  disabled = false,
  onSubmit,
  sldElements,
}: StarterCoordinationFormProps) {
  // The entries outlive a reload, Back and the sidebar for as long as the tab is open.
  const { activeRevisionId } = useProject();
  const revisionId = activeRevisionId();
  const { draft, setDraft, restored, clear, otherRevision, keepForThisRevision } =
    useStudyDraft<StarterCoordinationDraft>({
      module: STARTER_COORDINATION_DRAFT_MODULE,
      createInitial: createInitialStarterCoordinationDraft,
      schema: starterCoordinationDraftSchema,
      revisionId,
    });

  const [validationError, setValidationError] = useState<string | null>(null);
  const [rowToFocus, setRowToFocus] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const linkedSource = draft.faultLevelSource === "LINKED";

  // A new row sits among several fieldsets, so the cursor goes to it.
  useEffect(() => {
    if (rowToFocus === null) {
      return;
    }
    formRef.current
      ?.querySelector<HTMLInputElement>(`input[name="${rowToFocus}-motorCode"]`)
      ?.focus();
    setRowToFocus(null);
  }, [rowToFocus]);

  function updateStudy(patch: Partial<StarterCoordinationDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  // Stable for the selector's effects: it clears a run the listed revision does not hold.
  const setFaultRunId = useCallback(
    (faultRunId: string) => setDraft((current) => ({ ...current, faultRunId })),
    [setDraft],
  );

  function updateRow(id: string, patch: Partial<StarterRowDraft>) {
    setDraft((current) => ({
      ...current,
      rows: current.rows.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));
  }

  function addRow() {
    // A restored draft brings ids the counter of this page load has not handed out yet.
    seedStarterRowIdCounter(draft.rows.map((row) => row.id));
    const row = createStarterRowDraft();
    setDraft((current) => ({ ...current, rows: [...current.rows, row] }));
    setRowToFocus(row.id);
  }

  // A row nobody has typed in yet: the prefill may replace it, never a typed one.
  const isBlankRow = (row: StarterRowDraft): boolean =>
    Object.entries(row).every(([field, value]) => field === "id" || value === "");

  // The MCC bus picked from the diagram: while the starter list is empty or holds blank rows
  // only, one row per MOTOR load fed from that bus, its code and rated kW filled (A27 (f)).
  function chooseBus(busCode: string) {
    const motors = sldElementsOf(sldElements, ["load"], ["MOTOR"]).filter(
      (element) => element.bus === busCode,
    );
    setDraft((current) => {
      if (motors.length === 0 || !current.rows.every(isBlankRow)) {
        return { ...current, busCode };
      }
      seedStarterRowIdCounter(current.rows.map((row) => row.id));
      const rows = motors.map((motor) => ({
        ...createStarterRowDraft(),
        motorCode: motor.code,
        ratedPowerKw: motor.rating ?? "",
      }));
      return { ...current, busCode, rows };
    });
  }

  function removeRow(id: string) {
    setDraft((current) => ({ ...current, rows: current.rows.filter((row) => row.id !== id) }));
  }

  // Empties the form only; a result already on the page stays until "Clear results".
  function clearForm() {
    setValidationError(null);
    clear();
  }

  // The selector's own sentence stands alone; every other issue is read by its label.
  function describeIssue(issue: ValidationIssueLike): string {
    if (issue.code === "custom" && issue.path.at(-1) === "fault_run_id") {
      return issue.message;
    }
    return describeValidationIssue(issue, STARTER_COORDINATION_LABELS);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);

    const parsed = starterCoordinationRunCreateRequestSchema.safeParse(
      buildStarterCoordinationRunPayload(draft),
    );

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(
        issue ? describeIssue(issue) : "Review the starter coordination inputs.",
      );
      return;
    }

    await onSubmit(parsed.data);
  }

  const studyText = (label: string, field: keyof StarterCoordinationDraft, name: string) => (
    <label>
      {label}
      <input
        name={name}
        value={draft[field] as string}
        onChange={(event) =>
          updateStudy({ [field]: event.target.value } as Partial<StarterCoordinationDraft>)
        }
      />
    </label>
  );

  // Field names carry the row's id ("starter-3-motorCode"), stable across add and remove.
  const rowText = (
    row: StarterRowDraft,
    label: string,
    field: keyof StarterRowDraft,
    inputMode: "decimal" | "text" = "decimal",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={`${row.id}-${field}`}
        value={row[field]}
        onChange={(event) => updateRow(row.id, { [field]: event.target.value })}
      />
    </label>
  );

  // A select with "Choose…" first and the contract's values after it.
  const rowChoice = <T extends string>(
    row: StarterRowDraft,
    label: string,
    field: keyof StarterRowDraft,
    options: readonly T[],
    labels: Record<T, string>,
  ) => (
    <label>
      {label}
      <select
        name={`${row.id}-${field}`}
        value={row[field]}
        onChange={(event) => updateRow(row.id, { [field]: event.target.value })}
      >
        <option value="">{CHOOSE}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels[option]}
          </option>
        ))}
      </select>
    </label>
  );

  const busCode = draft.busCode.trim();

  return (
    <form aria-label="Starter coordination inputs" ref={formRef} onSubmit={handleSubmit}>
      {restored ? <p role="status">Draft restored from this session.</p> : null}
      {otherRevision ? (
        <OtherRevisionDraftNotice
          onKeep={keepForThisRevision}
          onClear={clearForm}
          disabled={disabled}
        />
      ) : null}

      <fieldset disabled={disabled}>
        <legend>Study definition</legend>
        {studyText("Study code", "code", "studyCode")}
        {studyText("Study name", "name", "studyName")}
        <SldElementPicker
          label="Bus code"
          name="busCode"
          value={draft.busCode}
          onChange={chooseBus}
          categories={["bus"]}
          elements={sldElements}
        />
        <label>
          Jurisdiction profile
          <select
            name="jurisdictionProfile"
            value={draft.jurisdictionProfile}
            onChange={(event) => updateStudy({ jurisdictionProfile: event.target.value })}
          >
            <option value="IN">India (CEA Regulations, IS, CPWD)</option>
            <option value="IEC">IEC (international, no national layer)</option>
            <option value="UK">United Kingdom (BS 7671) - reference data pending</option>
            <option value="EU">European Union (HD 60364) - reference data pending</option>
            <option value="US">United States (NEC / NFPA 70) - reference data pending</option>
            <option value="AU_NZ">
              Australia / New Zealand (AS/NZS 3000) - reference data pending
            </option>
          </select>
        </label>
        <p data-profile-note="true">{PROFILE_NOTE}</p>
        {studyText("Study notes", "notes", "studyNotes")}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Fault level</legend>
        <label>
          Fault level source
          <select
            name="faultLevelSource"
            value={draft.faultLevelSource}
            onChange={(event) =>
              updateStudy({ faultLevelSource: event.target.value as FaultLevelSource })
            }
          >
            {FAULT_LEVEL_SOURCES.map((source) => (
              <option key={source} value={source}>
                {FAULT_LEVEL_SOURCE_LABELS[source]}
              </option>
            ))}
          </select>
        </label>

        {linkedSource ? (
          <FaultRunSelector
            revisionId={revisionId}
            faultRunId={draft.faultRunId}
            onChange={setFaultRunId}
            busCode={busCode}
            withPeak={false}
          />
        ) : (
          <>
            {studyText(
              "Prospective current Ik'' (kA)",
              "typedProspectiveCurrentKa",
              "typedProspectiveCurrentKa",
            )}
            {studyText(
              "System nominal voltage (V)",
              "typedSystemNominalVoltageV",
              "typedSystemNominalVoltageV",
            )}
            {studyText("Fault bus code", "typedFaultBusCode", "typedFaultBusCode")}
            <p data-typed-hint="true">{TYPED_HINT}</p>
          </>
        )}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Starters</legend>
        {draft.rows.map((row, index) => {
          const number = index + 1;
          const nonDol = row.startingMethod !== "" && row.startingMethod !== "DOL";
          return (
            <fieldset key={row.id} data-starter-row={row.id}>
              <legend>Starter {number}</legend>

              <fieldset>
                <legend>Motor</legend>
                <SldElementPicker
                  label="Motor code"
                  name={`${row.id}-motorCode`}
                  value={row.motorCode}
                  onChange={(motorCode) => updateRow(row.id, { motorCode })}
                  categories={["load"]}
                  kinds={["MOTOR"]}
                  elements={sldElements}
                />
                {rowText(row, "Rated output (kW)", "ratedPowerKw")}
                {rowText(row, "Full-load current FLC (A)", "fullLoadCurrentA")}
                <p data-flc-hint="true">{FLC_HINT}</p>
                {rowChoice(
                  row,
                  "Starting method",
                  "startingMethod",
                  startingMethodSchema.options,
                  STARTING_METHOD_LABELS,
                )}
                {nonDol ? <p data-non-dol-hint="true">{NON_DOL_HINT}</p> : null}
              </fieldset>

              <fieldset>
                <legend>Coordination evidence</legend>
                {rowChoice(
                  row,
                  "Required coordination",
                  "requiredCoordinationType",
                  starterCoordinationTypeSchema.options,
                  COORDINATION_TYPE_LABELS,
                )}
                {rowChoice(
                  row,
                  "Declared coordination",
                  "declaredCoordinationType",
                  starterCoordinationTypeSchema.options,
                  COORDINATION_TYPE_LABELS,
                )}
                {rowText(row, "Certified Iq (kA)", "certifiedIqKa")}
                {rowText(row, "Test voltage Ue (V)", "testVoltageV")}
                {rowText(row, "Certificate reference", "certificateReference", "text")}
                <p data-certificate-hint="true">{CERTIFICATE_HINT}</p>
              </fieldset>

              <fieldset>
                <legend>Devices</legend>
                {rowChoice(
                  row,
                  "SCPD type",
                  "scpdKind",
                  scpdKindSchema.options,
                  SCPD_KIND_LABELS,
                )}
                {rowText(row, "SCPD rating (A)", "scpdRatingA")}
                {rowText(row, "Contactor AC-3 rating (A)", "contactorAc3RatingA")}
                {rowText(row, "Overload setting minimum (A)", "overloadSettingMinA")}
                {rowText(row, "Overload setting maximum (A)", "overloadSettingMaxA")}
              </fieldset>

              {draft.rows.length > 1 ? (
                <button type="button" onClick={() => removeRow(row.id)}>
                  Remove starter {number}
                </button>
              ) : null}
            </fieldset>
          );
        })}
        <button type="button" onClick={addRow}>
          Add starter
        </button>
      </fieldset>

      {validationError ? <p role="alert">{validationError}</p> : null}

      <button disabled={disabled} type="submit">
        Check starter coordination
      </button>
      <button disabled={disabled} type="button" onClick={clearForm}>
        Clear form
      </button>
    </form>
  );
}

export default StarterCoordinationForm;
